// ColonyController + bindColonyRejection (colony-screen design D3/D7/D8;
// client-colony "Mutation dispatch seam and rejection surfacing"). This module
// is the canonical home of the `ColonyController`/`ScriptingMutation` contract
// that `test/fake-colony-controller.ts` (tasks.md 1.2) declared ahead of this
// real implementation ("the real controller must satisfy this same shape so
// the fake is a drop-in substitute at every injection site") — the fake now
// re-exports these types from here rather than the other way around, so the
// seam has one canonical definition.
//
// `createColonyController` is a thin wrapper over the shared session-scoped
// connection's `sendMutation` (design D3): it opens no connection of its own,
// consistent with `Colony.tsx` never constructing a `Connection` (that
// production wiring is task group 9). `bindColonyRejection` is the store-facing
// half — wired as the same connection's `onMutationRejected` callback
// (alongside `bindColonyMessages` for `onServerMessage`, per
// `docs/dev/architecture/planned/colony-screen.md`'s `colony-screen-1`
// diagram) — it routes a non-upgrade `Rejected` onto the colony slice's
// `mutationRejection` so `Colony.tsx` can surface it against the open type
// sheet's `PromptBox`. The wire carries no per-mutation correlation id (D8),
// so this is a best-effort "most recent rejection" surface, not attributed to
// a specific dispatch. `Rejected{upgrade}` never reaches here: `Connection`
// handles that terminally itself (client-wire) and never invokes
// `onMutationRejected` for it.

import type { ClientMessage, RejectReason } from '../../net/wire/messages';
import type { ClientStore } from '../../state/store';

/** The five scripting mutations `ColonyController.dispatch` carries — the shipped subset
 * of `ClientMessage` this change dispatches (DESIGN §6). `handEdit` joined the other four
 * with beekeeper-wire-and-hand-edit (previously excluded, "deferred desktop-first" — the
 * server-side HandEdit path was already fully wired; only this client surface was missing). */
export type ScriptingMutation = Extract<
  ClientMessage,
  { type: 'prompt' | 'rollback' | 'createType' | 'setBirthType' | 'handEdit' }
>;

/**
 * Sentinel `HandEdit.typeName` meaning "the rounds working document as a whole," dispatched by
 * the Code tab's degraded whole-document view (beekeeper-agent-chat, client-colony "A non-parsing
 * document degrades to the whole-document view" — without a repair surface a player locked out by
 * a broken header has no way back in). Never a real B++ type name: an identifier is always
 * non-empty (`ui/colony/document-slice.ts`'s `TYPE_HEADER_RE` only ever captures `\S+`), so the
 * empty string cannot collide with one. `rounds-prompt.ts`'s dispatch routing is the only reader —
 * this value is never sent to a server; it selects `POST /rounds/{sandbox}/document` over
 * `POST /rounds/{sandbox}/types/{typeName}/source` client-side, before either request is built.
 */
export const WHOLE_DOCUMENT_HAND_EDIT_TYPE_NAME = '';

/**
 * Sentinel `HandEdit.baseVersion` meaning "no rounds document version has been observed yet."
 * `rounds-sandbox-state`'s document version history is 1-based (its first cut is `v1`), so `0`
 * can never collide with a real ordinal — `rounds-prompt.ts` translates it to the HTTP layer's
 * own `baseVersion: null` ("no known base"; `POST .../source` and `POST .../document` both accept
 * it, CONTRACT.md §6). Meaningless in live mode, whose `HandEdit.baseVersion` always carries a
 * real `TypeMeta.activeVersion` (>= 1) instead.
 */
export const UNKNOWN_DOCUMENT_BASE_VERSION = 0;

/** The dispatch seam every mutation-sending UI component is injected with.
 * `dispatch` MUST NOT open its own connection — it is a thin wrapper over the
 * shared session-scoped `Connection.sendMutation`. */
export interface ColonyController {
  dispatch(mutation: ScriptingMutation): 'sent' | 'not-ready';
}

/** The subset of `Connection` `createColonyController` needs — production
 * usage passes the shared session's real `Connection`; tests can pass
 * anything satisfying this (a `Connection` over the mock transport, or a bare
 * object exposing just `sendMutation`). */
export interface MutationSender {
  sendMutation(mutation: ScriptingMutation): 'sent' | 'not-ready';
}

/**
 * Builds the production `ColonyController`: `dispatch` forwards straight to
 * `connection.sendMutation`, returning its definite `sent | not-ready` result
 * unchanged. Opens no connection of its own.
 */
export function createColonyController(connection: MutationSender): ColonyController {
  return {
    dispatch(mutation) {
      return connection.sendMutation(mutation);
    },
  };
}

/**
 * Builds the connection's `onMutationRejected` callback: routes a non-upgrade
 * `Rejected` onto the colony slice's `mutationRejection` (client-colony D8),
 * without attributing it to a specific dispatch — the wire carries no
 * correlation id. Wire this in alongside `bindColonyMessages(store)` at
 * session construction (production hookup, task group 9).
 */
export function bindColonyRejection(store: ClientStore): (reason: RejectReason) => void {
  return (reason: RejectReason): void => {
    store.applyMutationRejected(reason);
  };
}
